<template>
  <!-- Mobile overlay -->
  <Transition name="overlay">
    <div
      v-if="mobileOpen"
      @click="mobileOpen = false"
      class="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-30"
      aria-hidden="true"
    />
  </Transition>

  <!-- Mobile hamburger button -->
  <button
    class="lg:hidden fixed top-3 left-3 z-50 p-2 rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-sm text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
    @click="mobileOpen = !mobileOpen"
    :title="mobileOpen ? 'Fechar menu' : 'Abrir menu'"
  >
    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path v-if="mobileOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  </button>

  <!-- Floating sidebar card -->
  <nav
    class="fixed z-40 flex flex-col
           bg-white dark:bg-zinc-900
           border border-slate-200/80 dark:border-zinc-800
           rounded-2xl overflow-hidden
           shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.06)]
           dark:shadow-[0_8px_40px_-6px_rgba(0,0,0,0.55)]
           transition-all duration-300 ease-in-out"
    :class="[
      'top-4 left-4 bottom-4',
      collapsed ? 'w-[60px]' : 'w-[232px]',
      mobileOpen ? 'translate-x-0' : '-translate-x-[calc(100%+1.25rem)] lg:translate-x-0'
    ]"
  >
    <!-- ── Brand header ─────────────────────────────── -->
    <div
      class="flex items-center gap-2.5 px-3 pt-4 pb-3 flex-shrink-0"
      :class="{ 'justify-center': collapsed }"
    >
      <!-- Logo icon (clickable to expand when collapsed) -->
      <button
        v-if="collapsed"
        @click="$emit('toggle-collapse')"
        title="Expandir menu"
        class="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 focus:outline-none"
      >
        <img :src="iconeAvance" alt="Avance" class="w-8 h-8 rounded-xl" />
      </button>
      <div
        v-else
        class="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
      >
        <img :src="iconeAvance" alt="Avance" class="w-8 h-8 rounded-xl" />
      </div>
      <Transition name="fade-text">
        <div v-if="!collapsed" class="min-w-0 flex-1">
          <img :src="logoAvanceResumida" alt="AgendAvance" class="h-7 w-auto" />
        </div>
      </Transition>
      <!-- Collapse button (only when expanded) -->
      <Transition name="fade-text">
        <button
          v-if="!collapsed"
          @click="$emit('toggle-collapse')"
          title="Recolher menu"
          class="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-slate-400 dark:text-zinc-600
                 hover:text-slate-700 dark:hover:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </Transition>
    </div>

    <!-- Divider -->
    <div class="mx-3 mb-2 h-px bg-slate-100 dark:bg-zinc-800 flex-shrink-0" />

    <!-- ── Navigation items ──────────────────────────── -->
    <div class="flex-1 overflow-y-auto scrollbar-thin px-2 space-y-0.5 pb-2">
      <router-link
        v-for="item in menuItems"
        :key="item.name"
        :to="item.to"
        :title="collapsed ? (item.locked ? `${item.label} (bloqueado — assinatura cancelada)` : item.label) : undefined"
        @click="handleItemClick($event, item)"
        class="group flex items-center rounded-xl text-sm font-medium transition-all duration-150"
        :class="[
          collapsed ? 'justify-center h-10 w-full' : 'gap-3 px-2.5 py-2.5',
          item.locked
            ? 'text-slate-300 dark:text-zinc-700 cursor-not-allowed'
            : isActiveRoute(item.name)
              ? 'text-white bg-primary-500 dark:bg-primary-500'
              : 'text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-100 hover:bg-slate-50 dark:hover:bg-zinc-800'
        ]"
      >
        <component
          :is="item.icon"
          class="flex-shrink-0 transition-colors duration-150"
          style="width:18px;height:18px"
          :class="item.locked
            ? 'text-slate-300 dark:text-zinc-700'
            : isActiveRoute(item.name)
              ? 'text-white'
              : 'text-slate-400 dark:text-zinc-500 group-hover:text-slate-600 dark:group-hover:text-zinc-300'"
        />
        <Transition name="fade-text">
          <span v-if="!collapsed" class="flex-1 truncate">{{ item.label }}</span>
        </Transition>
        <!-- Cadeado (assinatura cancelada) -->
        <Transition name="fade-text">
          <svg v-if="!collapsed && item.locked" class="w-3.5 h-3.5 flex-shrink-0 text-slate-300 dark:text-zinc-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </Transition>
        <!-- Active dot -->
        <Transition name="fade-text">
          <span
            v-if="!collapsed && !item.locked && isActiveRoute(item.name)"
            class="w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0"
          />
        </Transition>
      </router-link>
    </div>

    <!-- Divider -->
    <div class="mx-3 mt-1 h-px bg-slate-100 dark:bg-zinc-800 flex-shrink-0" />

    <!-- ── Bottom actions ──────────────────────────────── -->
    <div class="flex-shrink-0 px-2 py-2 space-y-0.5">
      <!-- Mobile (abaixo de lg, Navbar em gaveta): avatar + Novidades ficam
           aqui, acima da versão, em vez da barra fixa no canto da tela — ver
           TopBar.vue placement="sidebar" e App.vue (que só monta a flutuante
           a partir de lg; uma instância por vez). -->
      <div v-if="!isDesktop" class="pb-1">
        <TopBar placement="sidebar" :collapsed="collapsed" />
      </div>

      <!-- Versão da aplicação (última publicada, ver GET /auth/validate
           latest_app_version — releases é owner-only, mas a versão em si
           chega pra qualquer staff via fastify.knexAdmin, ver
           src/utils/appVersion.js).
           Ícone e texto num único bloco (inline-flex), e é esse bloco que fica
           no centro do navbar — nos dois eixos. -->
      <div
        v-if="latestAppVersion"
        class="w-full flex items-center justify-center rounded-xl text-sm font-medium text-slate-400 dark:text-zinc-600"
        :class="collapsed ? 'h-10' : 'px-2.5 py-2.5'"
        :title="`Versão ${latestAppVersion}`"
      >
        <span class="inline-flex items-center gap-3 min-w-0 max-w-full">
          <component :is="VersioningIcon" class="flex-shrink-0" style="width:18px;height:18px" />
          <Transition name="fade-text">
            <span v-if="!collapsed" class="min-w-0 truncate">Versão {{ latestAppVersion }}</span>
          </Transition>
        </span>
      </div>

      <!-- Language selector -->
      <!-- TODO: reativar seletor de idioma quando necessário
      <div class="relative lang-menu-root">
        <button
          @click="langMenuOpen = !langMenuOpen"
          :title="m.actions.language"
          class="w-full flex items-center rounded-xl text-sm font-medium transition-all duration-150
                 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-100 hover:bg-slate-50 dark:hover:bg-zinc-800"
          :class="collapsed ? 'justify-center h-10' : 'gap-3 px-2.5 py-2.5'"
        >
          <template v-if="collapsed">
            <FlagIcon :locale="localeStore.locale" cls="w-5 h-3.5 rounded-sm shadow-sm" />
          </template>
          <template v-else>
            <svg class="flex-shrink-0 text-slate-400 dark:text-zinc-500" style="width:18px;height:18px" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <div class="flex flex-1 items-center justify-between min-w-0">
              <span>{{ m.actions.language }}</span>
              <span class="flex items-center gap-1.5">
                <FlagIcon :locale="localeStore.locale" cls="w-5 h-3.5 rounded-sm shadow-sm" />
                <svg class="w-3 h-3 text-slate-400 dark:text-zinc-600 transition-transform duration-150" :class="langMenuOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </div>
          </template>
        </button>

        <Transition name="lang-drop">
          <div
            v-if="langMenuOpen"
            class="absolute z-50 rounded-xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-xl overflow-hidden py-1"
            :class="collapsed ? 'left-full ml-2 bottom-0 w-44' : 'left-2 right-2 bottom-full mb-1'"
          >
            <button
              v-for="loc in LOCALES" :key="loc.value"
              @click="localeStore.setLocale(loc.value); langMenuOpen = false"
              class="w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-100"
              :class="localeStore.locale === loc.value
                ? 'bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 font-semibold'
                : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800'"
            >
              <FlagIcon :locale="loc.value" cls="w-5 h-3.5 rounded-sm shadow-sm flex-shrink-0" />
              <span class="flex-1 text-left">{{ loc.country }}</span>
              <svg v-if="localeStore.locale === loc.value" class="w-3.5 h-3.5 flex-shrink-0 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        </Transition>
      </div>
      -->
    </div>

  </nav>


</template>

<script setup lang="ts">
import { ref, computed, h, onMounted, onUnmounted } from 'vue'
import iconeAvance from '../../assets/logo_avance_icone.svg'
import logoAvanceResumida from '../../assets/logo_resumida.svg'

interface AuthUser { id: number; name: string; email: string; role: string; latest_app_version?: string | null }
import { useRoute } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useToast } from '../../composables/useToast'
import { useLocaleStore, LOCALES } from '../../stores/locale.js'
import { getMessages } from '../../i18n/index.js'
import FlagIcon from '../common/FlagIcon.vue'
import TopBar from './TopBar.vue'
import { useMediaQuery } from '../../composables/useMediaQuery.js'

const props = defineProps<{ collapsed: boolean }>()
// lg = 1024px, mesmo breakpoint em que o Navbar deixa de ser gaveta.
const isDesktop = useMediaQuery('(min-width: 1024px)')
defineEmits(['toggle-collapse'])

const route      = useRoute()
const authStore  = useAuthStore()
const mobileOpen = ref(false)
const { warning } = useToast()

const localeStore = useLocaleStore()
const m = computed(() => getMessages(localeStore.locale))
const langMenuOpen = ref(false)

const closeLangMenu = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.closest('.lang-menu-root')) langMenuOpen.value = false
}
onMounted(() => document.addEventListener('mousedown', closeLangMenu))
onUnmounted(() => document.removeEventListener('mousedown', closeLangMenu))

// ─── Icon Components ─────────────────────────────────────────────
// Ícones sólidos (preenchidos), desenhados sob medida — sem lib externa.
const solid = (...shapes: [string, Record<string, string | number>][]) => () => h(
  'svg',
  { viewBox: '0 0 24 24', fill: 'currentColor' },
  shapes.map(([tag, attrs]) => h(tag, attrs)),
)

const DashboardIcon = solid(
  ['rect', { x: 3, y: 3, width: 7, height: 8, rx: 1.5 }],
  ['rect', { x: 12, y: 3, width: 9, height: 5, rx: 1.5 }],
  ['rect', { x: 12, y: 10, width: 9, height: 11, rx: 1.5 }],
  ['rect', { x: 3, y: 13, width: 7, height: 8, rx: 1.5 }],
)
const UserIcon = solid(
  ['circle', { cx: 12, cy: 8, r: 4 }],
  ['path', { d: 'M4 21a8 8 0 0116 0z' }],
)
const EstablishmentIcon = solid(
  ['rect', { x: 3, y: 10, width: 7, height: 11, rx: 1 }],
  ['rect', { x: 11, y: 4, width: 10, height: 17, rx: 1 }],
)
const ProfessionalIcon = solid(
  ['circle', { cx: 12, cy: 8, r: 4 }],
  ['path', { d: 'M4 21a8 8 0 0116 0z' }],
)
const ServiceIcon = solid(
  ['rect', { x: 5, y: 4, width: 14, height: 18, rx: 2 }],
  ['rect', { x: 9, y: 2, width: 6, height: 4, rx: 1 }],
  ['rect', { x: 8, y: 10, width: 8, height: 1.6, rx: 0.8, 'fill-opacity': 0.5 }],
  ['rect', { x: 8, y: 14, width: 8, height: 1.6, rx: 0.8, 'fill-opacity': 0.5 }],
  ['rect', { x: 8, y: 18, width: 5, height: 1.6, rx: 0.8, 'fill-opacity': 0.5 }],
)
const ClientIcon = solid(
  ['circle', { cx: 16.8, cy: 6.8, r: 2.7, 'fill-opacity': 0.55 }],
  ['path', { d: 'M13.6 10.4 L20 10.4 L21.4 16.3 L12.2 16.3 Z', 'fill-opacity': 0.55 }],
  ['circle', { cx: 7, cy: 6, r: 3.1 }],
  ['path', { d: 'M3.6 10.2 L10.4 10.2 L12.1 17.3 L1.9 17.3 Z' }],
)
const AppointmentIcon = solid(
  ['rect', { x: 3, y: 6, width: 18, height: 15, rx: 3 }],
  ['rect', { x: 7, y: 2, width: 2, height: 5, rx: 1 }],
  ['rect', { x: 15, y: 2, width: 2, height: 5, rx: 1 }],
)
const FinancialIcon = solid(
  ['path', { 'fill-rule': 'evenodd', d: 'M12 2a10 10 0 100 20 10 10 0 000-20zm1 3.4h-2v1.55c-1.6.32-2.75 1.5-2.75 3.05 0 1.85 1.6 2.7 3.35 3.1 1.35.32 1.9.72 1.9 1.35 0 .6-.55 1-1.5 1-1.1 0-1.55-.5-1.6-1.15H7.55c.07 1.55 1.15 2.55 2.45 2.87V18.4h2v-1.53c1.65-.3 2.85-1.28 2.85-2.97 0-2-1.7-2.7-3.35-3.1-1.3-.32-1.9-.63-1.9-1.3 0-.58.5-1 1.4-1 .95 0 1.35.47 1.4 1.15h1.85c-.07-1.4-.95-2.4-2.25-2.72V5.4z' }],
)
const RelacionamentoIcon = solid(
  ['circle', { cx: 8, cy: 8, r: 4.2 }],
  ['circle', { cx: 16, cy: 8, r: 4.2 }],
  ['path', { d: 'M2.5 10 L12 21 L21.5 10 Z' }],
)
const SalesIcon = solid(
  ['path', { d: 'M3 3h8l10 10-8 8L3 11z' }],
)
// Círculo com ponto de interrogação (Ajuda).
const HelpIcon = solid(
  ['circle', { cx: 12, cy: 12, r: 9.5, 'fill-opacity': 0.5 }],
  ['path', { d: 'M9.6 9.4a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.5', fill: 'none', stroke: 'currentColor', 'stroke-width': 2.2, 'stroke-linecap': 'round' }],
  ['circle', { cx: 12, cy: 16.9, r: 1.3 }],
)
const ConfiguracoesIcon = solid(
  ['rect', { x: 3, y: 5, width: 18, height: 2.4, rx: 1.2, 'fill-opacity': 0.5 }],
  ['rect', { x: 3, y: 11, width: 18, height: 2.4, rx: 1.2, 'fill-opacity': 0.5 }],
  ['rect', { x: 3, y: 17, width: 18, height: 2.4, rx: 1.2, 'fill-opacity': 0.5 }],
  ['circle', { cx: 8, cy: 6.2, r: 2.6 }],
  ['circle', { cx: 16, cy: 12.2, r: 2.6 }],
  ['circle', { cx: 10, cy: 18.2, r: 2.6 }],
)
// Duas silhuetas lado a lado (cabeça redonda + ombros/torso trapezoidal,
// só linhas retas) — cada pessoa em tamanho quase real, sem sobrepor tanto
// a ponto de virar dois pontinhos.
const PeopleIcon = solid(
  ['circle', { cx: 16.8, cy: 6.8, r: 2.7, 'fill-opacity': 0.55 }],
  ['path', { d: 'M13.6 10.4 L20 10.4 L21.4 16.3 L12.2 16.3 Z', 'fill-opacity': 0.55 }],
  ['circle', { cx: 7, cy: 6, r: 3.1 }],
  ['path', { d: 'M3.6 10.2 L10.4 10.2 L12.1 17.3 L1.9 17.3 Z' }],
)
const MapIcon = solid(
  ['path', { 'fill-rule': 'evenodd', d: 'M12 2 C8.1 2 5 5.1 5 9 C5 14.2 12 22 12 22 C12 22 19 14.2 19 9 C19 5.1 15.9 2 12 2 Z M12 6.4 A2.6 2.6 0 1 0 12 11.6 A2.6 2.6 0 1 0 12 6.4 Z' }],
)
const BillingIcon = solid(
  ['rect', { x: 3, y: 6, width: 18, height: 13, rx: 2.5 }],
  ['rect', { x: 3, y: 10, width: 18, height: 3.2, 'fill-opacity': 0.4 }],
)
const LegalIcon = solid(
  ['path', { d: 'M12 2 L20 5 L20 12 L12 22 L4 12 L4 5 Z' }],
)
const RequestsIcon = solid(
  ['path', { d: 'M4 10 L8 3 L16 3 L20 10 Z', 'fill-opacity': 0.6 }],
  ['rect', { x: 4, y: 10, width: 16, height: 9, rx: 2 }],
)
const VersioningIcon = solid(
  ['circle', { cx: 12, cy: 10, r: 7 }],
  ['path', { d: 'M8 16 L5 22 L9 20 L12 22 L15 20 L19 22 L16 16 Z', 'fill-opacity': 0.85 }],
)
const BlogIcon = solid(
  ['rect', { x: 3, y: 4, width: 18, height: 16, rx: 2 }],
  ['rect', { x: 6, y: 7.5, width: 8, height: 2, rx: 1, 'fill-opacity': 0.55 }],
  ['rect', { x: 6, y: 11, width: 12, height: 1.6, rx: 0.8, 'fill-opacity': 0.4 }],
  ['rect', { x: 6, y: 14.2, width: 12, height: 1.6, rx: 0.8, 'fill-opacity': 0.4 }],
)

const user = computed<AuthUser | null>(() => authStore.user)

// Última versão publicada (ver GET /auth/validate latest_app_version) —
// substitui o antigo sino de Notificações geral, que migrou pra TopBar.vue
// (sino só de novidades) e pro menu do usuário lá (tema/perfil/sair).
const latestAppVersion = computed(() => user.value?.latest_app_version || null)

const menuItems = computed(() => {
  const nav = m.value.nav

  if (user.value?.role === 'PLATFORM_OWNER') {
    return [
      { name: 'PlatformDashboard', label: nav.platformDashboard, to: '/plataforma',         icon: DashboardIcon },
      { name: 'PlatformMapa',      label: nav.platformMap,       to: '/plataforma/mapa',    icon: MapIcon },
      { name: 'PlatformPessoas',   label: nav.people,            to: '/plataforma/pessoas', icon: PeopleIcon },
      { name: 'PlatformCobranca',  label: nav.billingGroup,      to: '/plataforma/cobranca', icon: BillingIcon },
      { name: 'PlatformFinanceiro', label: nav.platformFinancial, to: '/plataforma/financeiro', icon: FinancialIcon },
      { name: 'PlatformLegal',     label: nav.legal,             to: '/plataforma/legal',   icon: LegalIcon },
      { name: 'PlatformBlog',      label: nav.blog,              to: '/plataforma/blog',    icon: BlogIcon },
      { name: 'PlatformSolicitacoes', label: nav.requests, to: '/plataforma/solicitacoes', icon: RequestsIcon },
      { name: 'Novidades', label: nav.versioning, to: '/novidades', icon: VersioningIcon },
      { name: 'PlatformConfiguracoes', label: nav.settings, to: '/plataforma/configuracoes', icon: ConfiguracoesIcon },
    ]
  }

  const items = [
    { name: 'Dashboard',      label: nav.dashboard,      to: '/',               icon: DashboardIcon },
    { name: 'Appointments',   label: nav.appointments,   to: '/appointments',   icon: AppointmentIcon, feature: 'APPOINTMENTS' },
    { name: 'Establishments', label: nav.establishments, to: '/establishments', icon: EstablishmentIcon, feature: 'ESTABLISHMENTS' },
    { name: 'Services',       label: nav.services,       to: '/services',       icon: ServiceIcon, feature: 'SERVICES' },
    { name: 'Clients',        label: nav.clients,        to: '/clients',        icon: ClientIcon, feature: 'CLIENTS' },
    { name: 'Financial',      label: nav.financial,      to: '/financial',      icon: FinancialIcon, feature: 'FINANCIAL' },
    { name: 'Vendas',         label: nav.sales,          to: '/vendas',         icon: SalesIcon, feature: 'VENDAS' },
    { name: 'Relacionamento', label: nav.relationship,   to: '/relacionamento', icon: RelacionamentoIcon, feature: ['COUPONS', 'LOYALTY', 'COMMISSIONS'] },
  ]
  let result = items.filter(i => {
    if (!i.feature) return true
    return Array.isArray(i.feature) ? authStore.hasAnyFeature(i.feature) : authStore.hasFeature(i.feature)
  })
  if (user.value?.role === 'PROFESSIONAL') {
    result = result.filter(i => ['Dashboard', 'Appointments'].includes(i.name))
    if (authStore.hasFeature('VENDAS') && authStore.can('sales.view_own')) {
      result.push({ name: 'MySales', label: nav.mySales, to: '/minhas-vendas', icon: SalesIcon })
    }
    if (authStore.hasFeature('SERVICES') && authStore.can('services.edit_own')) {
      result.push({ name: 'MyServices', label: nav.myServices, to: '/meus-servicos', icon: ServiceIcon })
    }
    if (authStore.hasFeature('COMMISSIONS')) {
      result.push({ name: 'MyCommissions', label: nav.myCommissions, to: '/minhas-comissoes', icon: FinancialIcon })
    }
  } else if (user.value?.role === 'ADMIN') {
    result.push(
      { name: 'Configuracoes', label: 'Configurações', to: '/configuracoes', icon: ConfiguracoesIcon },
    )
  }
  // Perguntas frequentes (views/Ajuda.vue) — todos os perfis, por último.
  result.push({ name: 'Ajuda', label: nav.help, to: '/ajuda', icon: HelpIcon })
  // Assinatura cancelada ou pagamento pendente: só Configurações (Pagamentos/
  // Legal) fica acessível, e só existe pra ADMIN — os demais perfis ficam
  // com tudo bloqueado.
  return result.map(item => ({
    ...item,
    locked: authStore.subscriptionBlocked && item.name !== 'Configuracoes',
  }))
})

const isActiveRoute = (name: string) => route.name === name

const handleItemClick = (event: MouseEvent, item: { locked?: boolean; label: string }) => {
  if (item.locked) {
    event.preventDefault()
    warning('Assinatura cancelada', 'Reative sua assinatura em Configurações para acessar esta área.')
    return
  }
  mobileOpen.value = false
}
</script>

<style scoped>
.overlay-enter-active,
.overlay-leave-active { transition: opacity 0.25s ease; }
.overlay-enter-from,
.overlay-leave-to     { opacity: 0; }

.fade-text-enter-active,
.fade-text-leave-active { transition: opacity 0.15s ease; }
.fade-text-enter-from,
.fade-text-leave-to     { opacity: 0; }

.lang-drop-enter-active,
.lang-drop-leave-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.lang-drop-enter-from,
.lang-drop-leave-to     { opacity: 0; transform: translateY(4px); }
</style>
